import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import SEOMeta from '@/components/base/SEOMeta';
import SchemaOrg from '@/components/base/SchemaOrg';
import { movies } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2 font-heading">影片未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的影片不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const similarMovies = movies
    .filter((m) => m.id !== movie.id && (m.category === movie.category || m.tags.some((t) => movie.tags.includes(t))))
    .slice(0, 4);

  const categoryLabel = movie.category === 'movie' ? '电影' : movie.category === 'tv' ? '电视剧' : '动漫';

  return (
    <>
      <SEOMeta
        title={`${movie.title} - ${categoryLabel} | 屁屁影院在线观看`}
        description={`${movie.title}，${movie.year}年${movie.director}执导，${movie.actors.slice(0, 3).join('、')}主演。${movie.description.slice(0, 100)}...在屁屁影院免费在线观看高清${categoryLabel}。`}
        keywords={`${movie.title},${movie.title}在线观看,${movie.title}免费观看,${categoryLabel},${movie.tags.join(',')},屁屁影院`}
        ogType="article"
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: movie.title,
          description: movie.description,
          datePublished: `${movie.year}-01-01`,
          author: {
            '@type': 'Person',
            name: movie.director,
          },
          publisher: {
            '@type': 'Organization',
            name: '屁屁影院',
            url: 'https://www.ppcinema.com',
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://www.ppcinema.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: categoryLabel,
              item: `https://www.ppcinema.com/category/${movie.category}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: movie.title,
            },
          ],
        }}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="px-4 md:px-6 max-w-5xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: categoryLabel, to: `/category/${movie.category}` },
              { label: movie.title },
            ]}
          />

          <article className="pb-10">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-8">
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="relative overflow-hidden rounded-xl aspect-[2/3] bg-background-200">
                  <img
                    src={movie.image}
                    alt={movie.title}
                    title={movie.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-xl md:text-3xl font-bold text-foreground-900 mb-3 font-heading">
                  {movie.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="px-3 py-1 text-xs font-semibold rounded-full bg-primary-500 text-white whitespace-nowrap">
                    {movie.rating} 分
                  </span>
                  <span className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap">
                    {movie.year}
                  </span>
                  <span className="px-3 py-1 text-xs rounded-full bg-accent-100 text-accent-900 whitespace-nowrap">
                    {categoryLabel}
                  </span>
                  <span className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                    {movie.region}
                  </span>
                  <span className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                    {movie.updateInfo}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs rounded-full bg-background-100 border border-background-200/70 text-foreground-600 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="space-y-3 mb-6 p-4 rounded-xl bg-background-100 border border-background-200/70">
                  <InfoRow label="导演" value={movie.director} />
                  <InfoRow label="主演" value={movie.actors.join('、')} />
                  <InfoRow label="地区" value={movie.region} />
                  <InfoRow label="年份" value={`${movie.year}年`} />
                  <InfoRow label="状态" value={movie.updateInfo} />
                </div>

                <Link
                  to={`/category/${movie.category}`}
                  className="inline-flex items-center px-8 py-3 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line mr-2 text-lg"></i>
                  立即观看
                </Link>
              </div>
            </div>

            <section className="mb-8">
              <h2 className="text-lg font-bold text-foreground-900 mb-4 font-heading">
                <i className="ri-file-text-line text-primary-500 mr-2"></i>
                剧情简介
              </h2>
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                <p className="text-sm text-foreground-700 leading-relaxed">{movie.description}</p>
              </div>
            </section>

            {similarMovies.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-foreground-900 font-heading">
                    <i className="ri-thumb-up-line text-accent-500 mr-2"></i>
                    猜你喜欢
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-product-shop>
                  {similarMovies.map((m) => (
                    <MovieCard key={m.id} {...m} />
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>

      <Footer />
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <span className="text-foreground-500 min-w-[40px] whitespace-nowrap">{label}</span>
      <span className="text-foreground-900">{value}</span>
    </div>
  );
}